import type { CellInfo } from "@/apis/table/types";
import { getBegend } from "@/utils/begend";
import { AccountingPeriod } from "@/types/enums";

/**
 * 같은 tagKey 라도 기초/기말/구분없음(period) 별로 트리에 따로 노출되고,
 * 한 표에 롤이 여럿이면 롤마다 같은 태그가 따로 노출되므로,
 * 하이라이트/선택 식별자는 roleKey·tagKey·period(begend)를 합쳐서 구분한다.
 */
export const buildHighlightId = (
  roleKey: string,
  tagKey: string,
  begend: AccountingPeriod,
): string => `${roleKey}::${tagKey}::${begend}`;

export const getHighlightId = (
  roleKey: string,
  tagKey: string,
  tagKoLabel: string | null | undefined,
): string => buildHighlightId(roleKey, tagKey, getBegend(tagKoLabel));

/** 매칭에 필요한 셀 필드만 본다. 셀 목록 API 응답(CellInfo)을 그대로 넘기면 된다. */
type TaggedCell = Pick<CellInfo, "address" | "taggedInfos">;

export interface HighlightCriteria {
  roleKey: string;
  tagKey: string;
  begend: AccountingPeriod;
  /** 기초/기말로 갈라져 트리에 따로 보이는 태그면 셀 period 까지 비교한다. */
  periodScoped: boolean;
}

const hasTag = (cell: TaggedCell, tagKey: string) => {
  for (const tag of cell.taggedInfos.tags ?? []) {
    if (tag.key === tagKey) return true;
  }
  return false;
};

/**
 * roleKey 셀 중 tagKey 가 붙은 셀 전부(period 무관). "태그 삭제" 가 실제로 건드리는 집합과 같다.
 *
 * roleKey 에는 트리뷰의 `TreeViewTableItem.roleKey` 를 넘긴다. 백엔드가 트리의 롤 키를 셀
 * `tagged_infos.role_key` 원문(`str(cell.definition_role_key_vo)`, 정규화 없음)으로 만들기 때문에
 * 셀의 `taggedInfos.roleKey` 와 글자 그대로 같다.
 */
export const findCellsTaggedWith = <T extends TaggedCell>(
  cells: T[],
  roleKey: string,
  tagKey: string,
): T[] => {
  const matched: T[] = [];
  for (const cell of cells) {
    if (cell.taggedInfos.roleKey !== roleKey) continue;
    if (!hasTag(cell, tagKey)) continue;
    matched.push(cell);
  }
  return matched;
};

/** 트리 노드 hover·클릭 시 비출 셀. */
export const findHighlightCells = <T extends TaggedCell>(
  cells: T[],
  criteria: HighlightCriteria,
): T[] => {
  const matched: T[] = [];
  for (const cell of findCellsTaggedWith(
    cells,
    criteria.roleKey,
    criteria.tagKey,
  )) {
    if (criteria.periodScoped) {
      const cellPeriod = cell.taggedInfos.period ?? AccountingPeriod.NONE;
      if (cellPeriod !== criteria.begend) continue;
    }
    matched.push(cell);
  }
  return matched;
};

export const taggedCellCountKey = (roleKey: string, tagKey: string) =>
  `${roleKey}::${tagKey}`;

/** (roleKey, tagKey) 별 태그가 붙은 셀 수. 트리 항목마다 셀 전체를 훑지 않도록 한 번에 센다. */
export const countTaggedCellsByRoleAndTag = (
  cells: TaggedCell[],
): Map<string, number> => {
  const counts = new Map<string, number>();
  for (const cell of cells) {
    const roleKey = cell.taggedInfos.roleKey;
    if (!roleKey) continue;
    const countedTagKeys = new Set<string>();
    for (const tag of cell.taggedInfos.tags ?? []) {
      if (countedTagKeys.has(tag.key)) continue;
      countedTagKeys.add(tag.key);
      const key = taggedCellCountKey(roleKey, tag.key);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }
  return counts;
};
